import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { jwtDecode } from 'jwt-decode';
import { queryClient } from 'api/queryClient';
import { msalInstance } from 'config/authConfig';
import { env } from 'config/env';
import { getCurrentAccessToken, subscribeToAccessTokenChanged } from 'services/tokenService';

type AccessTokenClaims = {
  transUniUserId?: string;
  transUniRoles?: string;
  cr?: string;
  roles?: string | string[];
};

function getClaimsFromAccessToken(accessToken: string | null): AccessTokenClaims | null {
  if (!accessToken) {
    return null;
  }

  try {
    return jwtDecode<AccessTokenClaims>(accessToken);
  } catch (error) {
    console.error('Failed to extract claims from access token.', error);
    return null;
  }
}

function getRolesFromClaims(claims: AccessTokenClaims | null): string[] {
  const rolesClaim = claims?.transUniRoles ?? claims?.cr ?? claims?.roles;

  if (!rolesClaim) {
    return [];
  }

  return (Array.isArray(rolesClaim) ? rolesClaim : rolesClaim.split(','))
    .map((role) => role.trim())
    .filter(Boolean);
}

// ==============================|| AUTH HOOKS ||============================== //

export default function useAuth() {
  const { accounts } = useMsal();
  const [accessToken, setAccessToken] = useState(() => getCurrentAccessToken());
  const activeAccount = msalInstance.getActiveAccount();
  const account = activeAccount ?? (accounts.length === 1 ? accounts[0] : null);
  const claims = getClaimsFromAccessToken(accessToken);

  useEffect(() => subscribeToAccessTokenChanged(setAccessToken), []);

  if (!activeAccount && account) {
    msalInstance.setActiveAccount(account);
  }

  const logout = async () => {
    queryClient.clear();

    if (env.VITE_APP_LOCAL_DEV_AUTH === 'true') {
      return;
    }

    await msalInstance.logoutRedirect({
      account: account ?? undefined,
      postLogoutRedirectUri: '/'
    });
  };

  if (env.VITE_APP_LOCAL_DEV_AUTH === 'true') {
    return {
      isLoggedIn: true,
      user: {
        id: claims?.transUniUserId ?? '00000000-0000-0000-0000-000000000003',
        name: 'Local Dev Owner',
        email: 'local-dev@example.com',
        roles: getRolesFromClaims(claims).length > 0 ? getRolesFromClaims(claims) : ['Owner']
      },
      logout
    };
  }

  return {
    isLoggedIn: !!account,
    user: account
      ? {
          id: claims?.transUniUserId,
          name: account.name || account.username,
          email: account.username,
          roles: getRolesFromClaims(claims)
        }
      : null,
    logout
  };
}
